/**
 * useAvatars — multi-avatar persona manager backed by /api/avatars (DB).
 *
 * Wcześniej trzymane w localStorage; po Sprint 3 migracja na DB.
 * Active avatar id nadal lokalnie (per-przeglądarka — to tylko UI state).
 */
import { useCallback, useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, uploadFile } from "@/lib/queryClient";

export interface Avatar {
  id: string;
  userId: string;
  clientId: string | null;
  name: string;
  contextText: string;
  voiceRefs: string[];
  photoUrl: string | null;
  photoStoragePath: string | null;
  usePhotoByDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AvatarUpsertInput {
  name: string;
  contextText?: string;
  voiceRefs?: string[];
  photoUrl?: string | null;
  photoStoragePath?: string | null;
  usePhotoByDefault?: boolean;
  clientId?: string | null;
}

const ACTIVE_KEY = "viralpartners.avatars.activeId.v1";
const USAGE_KEY = "viralpartners.avatars.usage.v1";
const PINNED_KEY = "viralpartners.avatars.pinned.v1";
const QUERY_KEY = ["avatars"] as const;

interface AvatarUsageRecord {
  count: number;
  lastUsedAt: string;
}

type AvatarUsageMap = Record<string, AvatarUsageRecord>;

function loadUsage(): AvatarUsageMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(USAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as AvatarUsageMap;
  } catch {
    return {};
  }
}

function saveUsage(usage: AvatarUsageMap): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(USAGE_KEY, JSON.stringify(usage));
}

export function useAvatars() {
  const qc = useQueryClient();
  const [activeId, setActiveIdState] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(ACTIVE_KEY);
  });
  const [usage, setUsage] = useState<AvatarUsageMap>(() => loadUsage());
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(() => {
    if (typeof window === "undefined") return new Set();
    try {
      const raw = window.localStorage.getItem(PINNED_KEY);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return new Set();
      return new Set(parsed.filter((v): v is string => typeof v === "string"));
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    saveUsage(usage);
  }, [usage]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(PINNED_KEY, JSON.stringify(Array.from(pinnedIds)));
  }, [pinnedIds]);

  const togglePin = useCallback((id: string): void => {
    setPinnedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const { data: avatars = [] } = useQuery<Avatar[]>({
    queryKey: QUERY_KEY,
    queryFn: () => apiRequest("GET", "/api/avatars"),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (activeId) window.localStorage.setItem(ACTIVE_KEY, activeId);
    else window.localStorage.removeItem(ACTIVE_KEY);
  }, [activeId]);

  const createMutation = useMutation({
    mutationFn: (input: AvatarUpsertInput) =>
      apiRequest("POST", "/api/avatars", input) as Promise<Avatar>,
    onSuccess: (created) => {
      qc.setQueryData<Avatar[]>(QUERY_KEY, (prev) => (prev ? [created, ...prev] : [created]));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<AvatarUpsertInput> }) =>
      apiRequest("PATCH", `/api/avatars/${id}`, patch) as Promise<Avatar>,
    onSuccess: (updated) => {
      qc.setQueryData<Avatar[]>(QUERY_KEY, (prev) =>
        prev ? prev.map((a) => (a.id === updated.id ? updated : a)) : [updated]
      );
    },
  });

  const shareMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("POST", `/api/avatars/${id}/share`) as Promise<{ shareUrl: string; key: string }>,
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("POST", `/api/avatars/${id}/duplicate`) as Promise<Avatar>,
    onSuccess: (created) => {
      qc.setQueryData<Avatar[]>(QUERY_KEY, (prev) => (prev ? [created, ...prev] : [created]));
    },
  });

  const uploadPhotoMutation = useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }): Promise<Avatar> => {
      const formData = new FormData();
      formData.append("photo", file);
      const res = await uploadFile(`/api/avatars/${id}/photo`, formData);
      return res as Avatar;
    },
    onSuccess: (updated) => {
      qc.setQueryData<Avatar[]>(QUERY_KEY, (prev) =>
        prev ? prev.map((a) => (a.id === updated.id ? updated : a)) : [updated]
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/avatars/${id}`),
    onSuccess: (_data, id) => {
      qc.setQueryData<Avatar[]>(QUERY_KEY, (prev) => (prev ? prev.filter((a) => a.id !== id) : []));
      setActiveIdState((prev) => (prev === id ? null : prev));
    },
  });

  const createAvatar = useCallback(
    async (input: AvatarUpsertInput): Promise<Avatar> => {
      return createMutation.mutateAsync(input);
    },
    [createMutation]
  );

  const updateAvatar = useCallback(
    async (id: string, patch: Partial<AvatarUpsertInput>): Promise<void> => {
      await updateMutation.mutateAsync({ id, patch });
    },
    [updateMutation]
  );

  const deleteAvatar = useCallback(
    async (id: string): Promise<void> => {
      await deleteMutation.mutateAsync(id);
    },
    [deleteMutation]
  );

  const uploadPhoto = useCallback(
    async (id: string, file: File): Promise<Avatar> => {
      return uploadPhotoMutation.mutateAsync({ id, file });
    },
    [uploadPhotoMutation]
  );

  const duplicateAvatar = useCallback(
    async (id: string): Promise<Avatar> => {
      return duplicateMutation.mutateAsync(id);
    },
    [duplicateMutation]
  );

  const shareAvatar = useCallback(
    async (id: string): Promise<string> => {
      const result = await shareMutation.mutateAsync(id);
      return result.shareUrl;
    },
    [shareMutation]
  );

  const setActiveAvatar = useCallback((id: string | null): void => {
    setActiveIdState(id);
    if (id) {
      setUsage((prev) => ({
        ...prev,
        [id]: {
          count: (prev[id]?.count ?? 0) + 1,
          lastUsedAt: new Date().toISOString(),
        },
      }));
    }
  }, []);

  const activeAvatar = activeId ? (avatars.find((a) => a.id === activeId) ?? null) : null;

  // If active avatar references an id that no longer exists, clear it
  // (avatar was deleted from another tab / by bulk delete in this one).
  useEffect(() => {
    if (activeId && avatars.length > 0 && !avatars.some((a) => a.id === activeId)) {
      setActiveIdState(null);
    }
  }, [activeId, avatars]);

  // Sort avatars: pinned first (most-recent within pinned), then unpinned by recency.
  const avatarsByRecency = [...avatars].sort((a, b) => {
    const ap = pinnedIds.has(a.id);
    const bp = pinnedIds.has(b.id);
    if (ap !== bp) return ap ? -1 : 1;
    const ua = usage[a.id]?.lastUsedAt;
    const ub = usage[b.id]?.lastUsedAt;
    if (ua && ub) return ub.localeCompare(ua);
    if (ua) return -1;
    if (ub) return 1;
    return a.name.localeCompare(b.name);
  });

  return {
    avatars: avatarsByRecency,
    avatarsRaw: avatars,
    activeId,
    activeAvatar,
    usage,
    pinnedIds,
    togglePin,
    createAvatar,
    updateAvatar,
    deleteAvatar,
    setActiveAvatar,
    uploadPhoto,
    duplicateAvatar,
    shareAvatar,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isUploadingPhoto: uploadPhotoMutation.isPending,
    isDuplicating: duplicateMutation.isPending,
    isSharing: shareMutation.isPending,
  };
}
